import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { SvgIcon } from 'common/react/components/SvgIcon';
import { ImageElement } from './image';
import { useGetUserAuthenticated } from 'common/react/hooks/use-get-user-authenticated';

export const MediaThumbnail = ({ video, index }) => {
    const ref = useRef();

    const { userAuthenticated } = useGetUserAuthenticated();

    const videoData = PULSE.app.common.content.getContentModel(
        video,
        PULSE.app.common.CONSTANTS.VARIANT_CONFIGURATIONS.VIDEO_HUB
    );

    const videoUrlParams = {
        limit: 50,
        tagNames: `ProgramCategory:${videoData.tags
            .find((item) => item.label.includes('ProgramCategory:'))
            ?.label.replace('ProgramCategory:', '')}`,
        references: ['AFL_COMPSEASON', 'AFL_ROUND']
            .map((reference) => {
                const refValue = video.references.find(
                    (item) => item.type === reference
                );
                if (refValue) {
                    return `${reference}:${refValue.id}`;
                }
                return null;
            })
            .filter((reference) => !!reference)
            .join(',')
    };

    const videoLink = `${videoData.link}&${PULSE.core.url.buildQueryString(
        videoUrlParams
    )}`;

    const isPremium = PULSE.app.common.content.isPremium(video);

    const videoModalData = {
        contentId: videoData.id,
        mediaId: videoData.mediaId,
        mediaTitle: PULSE.app.templating.helper.cleanString(videoData.title),
        mediaDescription: PULSE.app.templating.helper.cleanString(
            videoData.description
        ),
        subtitle: PULSE.app.templating.helper.cleanString(videoData.subtitle),
        type: videoData.type,
        link: videoLink,
        publishFrom: videoData.publishFrom + 1,
        mediaDate: PULSE.app.templating.helper.getDateNice(
            videoData.publishFrom
        )
    };

    const cardClickHandler = (evt) => {
        const cardLink = evt.target;
        const container = cardLink.closest('.video-hub');
        const cardLinks = container.querySelectorAll(
            '.js-media-thumbnail__link'
        );
        cardLink.title = container.querySelector(
            '.media-thumbnail__title'
        ).textContent;

        PULSE.app.tracking.trackCardClicks(
            container,
            cardLink,
            cardLinks.length
        );
    };

    useEffect(() => {
        if (ref.current && userAuthenticated !== null) {
            if (isPremium && !userAuthenticated) {
                PULSE.app.UpsellModalContainer?.createInstance(ref.current);
            } else {
                PULSE.app.VideoPlayerModal?.createInstance(ref.current);
            }
            ref.current.dataset.modalAdded = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userAuthenticated, ref.current]);

    // if video is premium, wait for auth result before render
    if (isPremium && userAuthenticated === null) {
        return;
    }

    // eslint-disable-next-line consistent-return
    return (
        <article
            className={`media-thumbnail ${
                isPremium ? 'media-thumbnail--premium' : ''
            }`}
        >
            <div className="media-thumbnail__link">
                <header
                    className={`media-thumbnail__image-container placeholder placeholder--small placeholder--dark`}
                >
                    <div className="js-lazy-load lazy-image-wrapper is-loaded">
                        <ImageElement
                            ImageUrl={videoData.onDemandUrl}
                            ImageTitle={videoData.title}
                        />
                    </div>

                    <div className={`media-thumbnail__video-duration`}>
                        <SvgIcon
                            icon={'video-play-small'}
                            className={`icon media-thumbnail__duration-icon`}
                        />
                        <span className={`media-thumbnail__duration-text`}>
                            {PULSE.app.common.date.durationToTime(
                                videoData.duration
                            )}
                        </span>
                    </div>
                </header>
                <div className={`media-thumbnail__caption`}>
                    <a
                        ref={ref}
                        className={`media-thumbnail__headline-link js-media-thumbnail__link`}
                        data-ui-modal={`${
                            isPremium && !userAuthenticated
                                ? '#UpsellModal'
                                : '#VideoModal'
                        }`}
                        href={videoLink}
                        data-content-id={videoData.id}
                        data-ui-args={JSON.stringify(videoModalData)}
                        data-index-position={index + 1}
                        onClick={cardClickHandler}
                        data-content-type={
                            PULSE.app.common.CONSTANTS.TRACKING.CONTENT_TYPES
                                .VIDEO
                        }
                    >
                        <h2 className={`media-thumbnail__title`}>
                            {videoData.title}
                        </h2>
                    </a>
                </div>
            </div>
        </article>
    );
};

MediaThumbnail.propTypes = {
    video: PropTypes.object,
    index: PropTypes.number
};
